import { mount, unmount } from 'svelte';
import Tooltip from './Tooltip.svelte';

export function tooltip(node: HTMLElement, text?: string) {
	let tooltipInstance: any = null;
	let showTimeout: number | undefined;
	let destroyed = false;

	// Create reactive state that will automatically sync with component
	const tooltipState = $state({
		text: text ?? node.getAttribute('aria-label') ?? '',
		visible: false,
		x: 0,
		y: 0
	});

	function show() {
		if (!tooltipState.text) return;
		if (tooltipInstance) return;

		tooltipInstance = mount(Tooltip, {
			target: document.body,
			props: tooltipState // Pass the reactive state object
		});

		positionTooltip();
		tooltipState.visible = true; // Just update the state directly

		window.addEventListener('scroll', positionTooltip, true);
		window.addEventListener('resize', positionTooltip);
	}

	function hide() {
		if (!tooltipInstance) return;

		const instance = tooltipInstance;
		tooltipInstance = null;

		window.removeEventListener('scroll', positionTooltip, true);
		window.removeEventListener('resize', positionTooltip);

		// Defer state mutation to avoid state_unsafe_mutation when hide()
		// is triggered by a blur event during Svelte's teardown cycle
		queueMicrotask(() => {
			if (!destroyed) {
				tooltipState.visible = false;
			}
			setTimeout(() => unmount(instance), 150);
		});
	}

	function positionTooltip() {
		if (!tooltipInstance) return;

		const rect = node.getBoundingClientRect();
		const gap = 8;

		let top = rect.bottom + gap;
		let left = rect.left + rect.width / 2;

		if (top + 50 > window.innerHeight) {
			top = rect.top - node.clientHeight - gap;
		}

		if (left < 32) left = 32;
		if (left > window.innerWidth - 100) {
			left = window.innerWidth - 100;
		}
		if (top < 0) top = gap;

		tooltipState.x = left;
		tooltipState.y = top;
	}

	function mouseEnter() {
		showTimeout = window.setTimeout(show, 300);
	}

	function mouseLeave() {
		if (showTimeout) {
			clearTimeout(showTimeout);
			showTimeout = undefined;
		}
		hide();
	}

	node.addEventListener('mouseenter', mouseEnter);
	node.addEventListener('mouseleave', mouseLeave);
	node.addEventListener('focus', show);
	node.addEventListener('blur', hide);

	// Watch for aria-label attribute changes only if no explicit text was provided
	let observer: MutationObserver;
	if (!text) {
		observer = new MutationObserver(() => {
			const newLabel = node.getAttribute('aria-label');
			if (newLabel && newLabel !== tooltipState.text) {
				tooltipState.text = newLabel;
			}
		});

		observer.observe(node, {
			attributes: true,
			attributeFilter: ['aria-label']
		});
	}

	return {
		update(newText: string) {
			tooltipState.text = newText; // Update state directly
		},
		destroy() {
			destroyed = true;
			hide();
			observer?.disconnect();
			node.removeEventListener('mouseenter', mouseEnter);
			node.removeEventListener('mouseleave', mouseLeave);
			node.removeEventListener('focus', show);
			node.removeEventListener('blur', hide);
		}
	};
}
